import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Gift, Crown, ShoppingBag, Bell, Mail, Heart, IndianRupee, ArrowRight } from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import { formatINR } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const CARDS = [
  { key: "revenue", label: "Order value", icon: IndianRupee, money: true },
  { key: "orders", label: "Orders", icon: ShoppingBag, to: "orders" },
  { key: "products", label: "Products", icon: Package, to: "products" },
  { key: "parcels", label: "Parcels", icon: Gift, to: "parcels" },
  { key: "founder", label: "Founder items", icon: Crown, to: "founder" },
  { key: "personalizations", label: "Gift pages", icon: Heart, to: "personalizations" },
  { key: "leads", label: "Notify leads", icon: Mail, to: "leads" },
  { key: "reminders", label: "Reminders", icon: Bell },
];

const statusColor = (s) => ({
  pending: "bg-wp-warning/15 text-wp-warning", confirmed: "bg-wp-ocean/15 text-wp-ocean",
  packed: "bg-wp-blush-100 text-wp-rose-strong", shipped: "bg-wp-mint/30 text-wp-ocean",
  delivered: "bg-wp-success/15 text-wp-success", cancelled: "bg-wp-ink/10 text-wp-ink-muted",
}[s] || "bg-wp-ink/10 text-wp-ink-muted");

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({ counts: {}, recent_orders: [] })); }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-2xl">Dashboard</h1>
      <p className="text-sm text-wp-ink-muted">A quick pulse of your store.</p>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {CARDS.map((c) => {
          const val = data?.counts?.[c.key];
          const inner = (
            <div className="rounded-wp-lg border border-border bg-card p-4 shadow-wp-soft transition-transform hover:-translate-y-0.5">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-wp-blush-100 text-wp-rose-strong"><c.icon className="h-4 w-4" /></span>
              <p className="mt-3 price-num text-2xl font-semibold">{data ? (c.money ? formatINR(val || 0) : (val ?? 0)) : <Skeleton className="h-7 w-12" />}</p>
              <p className="text-xs text-wp-ink-muted">{c.label}</p>
            </div>
          );
          return c.to ? <Link key={c.key} to={`/admin/${c.to}`}>{inner}</Link> : <div key={c.key}>{inner}</div>;
        })}
      </div>

      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-xl">Recent orders</h2>
          <Link to="/admin/orders" className="flex items-center gap-1 text-sm font-medium text-wp-rose-strong hover:underline">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft">
          {!data ? (
            <div className="space-y-2 p-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (data.recent_orders || []).length === 0 ? (
            <div className="p-8 text-center text-wp-ink-muted">No orders yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {data.recent_orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{o.customer?.name || "Guest"} · #{o.id.slice(0, 8)}</p>
                    <p className="text-xs text-wp-ink-muted">{(o.items || []).length} item(s) · {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`border-0 ${statusColor(o.status)}`}>{o.status}</Badge>
                    <span className="price-num text-sm font-semibold text-wp-rose-strong">{formatINR(o.subtotal)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
